/**
 * Lab bet-ticker Member cell marker — class names drive whole-row CSS
 * (`tr:has(.w7-bm-member--doubtfull|watchlist|reason-*)` in book-management.css).
 */

import {
  resolveTickerMemberFlag,
  type TickerMemberFlag,
} from '@/api/hooks/book/useBettickerMapping'
import type { BettickerMapping } from '@/api/hooks/book/bettickerMapping'
import {
  betfairReasonDotClass,
  betfairReasonRowClass,
  showBetfairReasonDot,
} from './betfairReason'

export function tickerMemberFlagClass(flag: TickerMemberFlag | null): string {
  if (flag === 'doubtfull') return 'w7-bm-member--doubtfull'
  if (flag === 'watchlist') return 'w7-bm-member--watchlist'
  return ''
}

export function tickerMemberCellClassName(
  flag: TickerMemberFlag | null,
  reasonClass: string | null = null,
): string {
  return ['w7-bm-member', tickerMemberFlagClass(flag), reasonClass]
    .filter(Boolean)
    .join(' ')
}

export type TickerMemberCellProps = {
  username: string | undefined | null
  clientIp: string | undefined | null
  label: string
  title?: string
  mapping: BettickerMapping | undefined
  doubtfull?: boolean
  watchlist?: boolean
  isFlagged?: boolean
  isBetfairFlagged?: boolean
  reason?: string
}

/** Renders Member label with Lab doubtful/watchlist + Betfair reason markers. */
export function TickerMemberCell({
  username,
  clientIp,
  label,
  title,
  mapping,
  doubtfull,
  watchlist,
  isFlagged,
  isBetfairFlagged,
  reason,
}: TickerMemberCellProps) {
  const flag = resolveTickerMemberFlag(username, clientIp, mapping, {
    doubtfull,
    watchlist,
  })
  const reasonClass = betfairReasonRowClass({
    reason,
    isFlagged,
    isBetfairFlagged,
    memberFlag: flag,
  })
  const reasonDotClass =
    showBetfairReasonDot({ isFlagged, isBetfairFlagged, reason }) &&
    betfairReasonDotClass(reason)
  return (
    <span
      className={tickerMemberCellClassName(flag, reasonClass)}
      title={title}
      data-testid="ticker-member-cell"
    >
      {reasonDotClass ? (
        <span
          className={`w7-bm-reason-dot ${reasonDotClass}`}
          title={reason}
          data-testid="betfair-reason-dot"
          aria-label={reason}
        />
      ) : null}
      {label}
    </span>
  )
}
